import React, { useMemo, useState } from 'react'
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../../firebase-config'
import TicketConversation from '../TicketConversation'

function useTickets() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  React.useEffect(() => {
    const q = query(collection(db, 'tickets'), orderBy('createdAt', 'desc'), limit(50))
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      setLoading(false)
    }, () => setLoading(false))
    return () => unsub()
  }, [])

  return { items, loading }
}

function fmt(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date(ts)
  if (!d || Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('es-AR')
}

export default function TicketsInbox() {
  const { items, loading } = useTickets()
  const [estado, setEstado] = useState('todos')
  const [busy, setBusy] = useState('')
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [cierreComentario, setCierreComentario] = useState('')
  const [sentimentHint, setSentimentHint] = useState('')
  const [toast, setToast] = useState(null)

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 2500)
  }

  const filtrados = useMemo(() => {
    if (estado === 'todos') return items
    return items.filter((x) => x.estado === estado)
  }, [items, estado])

  const handleEstado = async (ticketId, nextEstado) => {
    setBusy(ticketId)
    showToast('Actualizando ticket...', 'info')
    try {
      const callUpdate = httpsCallable(functions, 'updateTicketStatus')
      await callUpdate({
        ticketId,
        estado: nextEstado,
        cierreComentario: nextEstado === 'resuelto' ? cierreComentario.trim() : '',
        sentimentHint: sentimentHint || '',
      })
      if (nextEstado === 'resuelto') setCierreComentario('')
      showToast('Estado actualizado y auditado.', 'success')
    } catch (e) {
      showToast(e?.message || 'No se pudo actualizar el ticket.', 'error')
    } finally {
      setBusy('')
    }
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4 text-slate-100 shadow-[0_20px_50px_rgba(2,6,23,0.5)] md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h3 className="text-lg font-bold text-cyan-100">Gestion de Tickets</h3>
        <div className="flex flex-wrap gap-2">
          <select
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
          >
            <option value="todos">Todos</option>
            <option value="abierto">Abierto</option>
            <option value="en_progreso">En progreso</option>
            <option value="resuelto">Resuelto</option>
          </select>
          <select
            value={sentimentHint}
            onChange={(e) => setSentimentHint(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100"
          >
            <option value="">Tipo (opcional)</option>
            <option value="Error de Sistema">Error de Sistema</option>
            <option value="Duda Legal">Duda Legal</option>
          </select>
        </div>
      </div>
      {toast && (
        <p className={`mt-2 text-xs font-semibold ${toast.type === 'error' ? 'text-rose-400' : toast.type === 'info' ? 'text-slate-300' : 'text-emerald-400'}`}>
          {toast.message}
        </p>
      )}

      {loading && <p className="mt-3 text-sm text-slate-300">Cargando tickets...</p>}
      {!loading && filtrados.length === 0 && <p className="mt-3 text-sm text-slate-300">Sin tickets para este filtro.</p>}

      <div className="mt-3 grid gap-3 xl:grid-cols-[1.05fr_1fr]">
        <div className="max-h-[70vh] space-y-2 overflow-auto pr-1">
          {filtrados.map((t) => (
            <div
              key={t.id}
              onClick={() => setSelectedTicket(t)}
               className={`w-full cursor-pointer rounded-xl border p-3 text-left transition ${selectedTicket?.id === t.id ? 'border-cyan-500 bg-cyan-950/40' : 'border-slate-700 bg-slate-950 hover:border-cyan-700/70 hover:bg-cyan-950/20'}`}
            >
              <div className="flex flex-wrap items-center gap-2">
                 <span className="rounded bg-slate-800 px-2 py-0.5 text-xs font-semibold text-slate-200">{t.ticketCode || t.id}</span>
                 <span className="rounded bg-cyan-950 px-2 py-0.5 text-xs text-cyan-300">{t.estado || 'abierto'}</span>
                 {t.hasDictamenLink && <span className="rounded bg-amber-950 px-2 py-0.5 text-xs text-amber-300">Vinculado a dictamen</span>}
              </div>
               <p className="mt-1 text-sm font-semibold text-slate-100">{t.asunto || 'Sin asunto'}</p>
               <div className="mt-1 grid grid-cols-1 gap-1 text-xs text-slate-300 md:grid-cols-2">
                <p>Dictamen: {t.dictamenId || '-'}</p>
                <p>Normativa: {t.versionNormativa || '-'}</p>
                <p className="md:col-span-2">Creado: {fmt(t.createdAt)}</p>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                 <button type="button" disabled={busy === t.id} onClick={(e) => { e.stopPropagation(); handleEstado(t.id, 'en_progreso') }} className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800">Tomar</button>
                 <button type="button" disabled={busy === t.id} onClick={(e) => { e.stopPropagation(); handleEstado(t.id, 'resuelto') }} className="rounded-lg border border-emerald-800 bg-emerald-950 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-900">Resolver</button>
                 <button type="button" disabled={busy === t.id} onClick={(e) => { e.stopPropagation(); handleEstado(t.id, 'abierto') }} className="rounded-lg border border-amber-800 bg-amber-950 px-2 py-1 text-xs text-amber-300 hover:bg-amber-900">Reabrir</button>
              </div>
            </div>
          ))}
        </div>

        <div className="min-w-0">
          {selectedTicket ? (
            <TicketConversation ticket={selectedTicket} />
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-950 p-4 text-sm text-slate-300">
              Selecciona un ticket para ver el chat.
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
